/** @jsx jsx */
import { useEffect, useRef } from 'react'
import { useMapboxContext } from '../useMapboxContext'
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp'
import { useRouter } from 'next/router'
import { jsx, useThemeUI } from 'theme-ui'
import { Modal } from './Modal'

const Marker = ({
  location,
  active = false,
  options = {
    disableEase: false,
    disableZoom: false,
    disableCenter: false,
  },
}) => {
  const router = useRouter()
  const { map, markerBounds, zoom, locationZoom } = useMapboxContext()

  // Markers
  const marker = useRef()
  const previousMarker = useRef()
  const context = useThemeUI()

  const markerActive = `
      <svg data-name="marker-active" width="22" height="22" viewBox="0 0 21.5 21.5">
        <rect style="fill: ${context.theme.rawColors.red};" x="1.75" y="1.75" width="18" height="18"/>
        <polygon points="2.99 1.75 10.75 9.51 18.51 1.75 2.99 1.75"/>
        <polygon points="9.51 10.75 1.75 2.99 1.75 18.51 9.51 10.75"/>
        <polygon points="10.75 11.99 2.99 19.75 18.51 19.75 10.75 11.99"/>
        <polygon points="11.99 10.75 19.75 18.51 19.75 2.99 11.99 10.75"/>
        <path style="fill: ${context.theme.rawColors.red};" d="M19.75,1.75v18h-18v-18h18M21.5,0H0V21.5H21.5V0Z"/>
      </svg>
    `

  const markerInactive = `
        <svg
        data-name="marker-inactive"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 21.5 21.5"
        width="22"
        height="22"
        >
          <rect width="21.5" height="21.5"/>
          <path style="fill: ${context.theme.rawColors.red};" d="M19.75,1.75v18h-18v-18h18M21.5,0H0V21.5H21.5V0Z"/>
        </svg>
    `

  /**
   * UseEffect to create initial marker
   * Also includes cleanup to remove marker on unmount
   */
  useEffect(() => {
    const longLatCoord = [location.long, location.lat]

    const el = document.createElement('div')
    el.className = 'marker'
    el.style.width = '22px'
    el.style.height = '22px'
    el.style.cursor = 'pointer'

    // Add marker to map
    marker.current = new mapboxgl.Marker(el)
      .setLngLat(longLatCoord)
      .addTo(map.current)

    // Add property for us to track whether marker is selected
    marker.current.selected = false

    // Set marker icon
    if (active) {
      marker.current._element.innerHTML = markerActive
      previousMarker.current = marker.current
      marker.current.selected = true
    } else {
      marker.current._element.innerHTML = markerInactive
    }

    marker.current.getElement().addEventListener('click', evt => {
      if (marker.current.selected) {
        router.push(`/map`, undefined, { shallow: true })
      } else {
        router.push(`/map/${location.slug}`, undefined, { shallow: true })
      }
    })

    // Extend boundary that encompasses all markers
    markerBounds.current.extend(longLatCoord)

    return () => marker.current.remove()
  }, [])

  /**
   * useEffect to handle marker states
   * This is nessisary as the markers can not
   * be rendered easily as react components.
   */
  useEffect(() => {
    marker.current._element.innerHTML = active ? markerActive : markerInactive
    marker.current.selected = active ? true : false

    const newZoom = zoom < locationZoom ? locationZoom : zoom

    // If marker is active move to the correct location
    if (active) {
      const easeOptions = {
        padding: { bottom: 100 },
        duration: 750,
        ...(!options.disableCenter
          ? { center: [location.long, location.lat] }
          : {}),
        ...(!options.disableZoom ? { zoom: newZoom } : {}),
      }
      map.current.easeTo(easeOptions)
    }
  }, [active])

  /**
   * Marker component returns a modal if the marker is active
   * Otherwise the marker returns null as required by React to not render anything.
   */
  return active ? (
    <Modal
      location={location}
      onClose={() => router.push(`/map`, undefined, { shallow: true })}
    />
  ) : null
}

export { Marker }
